import { Dispatch, useCallback, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import { CONTENT_WARNING_BLOCK_ID } from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { Button } from 'UIPackage/MainModule/components/Button';
import { KnmAttachmentsSigning } from 'KnmPackage/MainModule/components/KnmAttachmentsSigning';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmData, getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { knmTryGenerateWarning } from 'KnmPmPackage/MainModule/actions/knmPmActions';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';

import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { IKnmFormRef } from '../../../KnmPackage/MainModule/interfaces/IKnmFormRef';

interface IPmContentWarningProps {
  className?: string;
  watchOnly: boolean;
  attachmentsNumber: number;
}

interface IPmContentWarningStoreProps {
  knmData: IKnm | undefined;
  settings: IAdminSetting[] | undefined;
  formRef: IKnmFormRef | undefined;
  isErknm: boolean;
}

const PmContentWarningWithoutStore = ({
  className = '',
  watchOnly,
  attachmentsNumber,
  knmData,
  settings,
  formRef,
  isErknm,
}: IPmContentWarningProps & IPmContentWarningStoreProps) => {
  const [isVisibleSignModal, setIsVisibleSignModal] = useState<boolean>(false);
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const openSignModal = useCallback(() => setIsVisibleSignModal(true), []);
  const closeSignModal = useCallback(() => setIsVisibleSignModal(false), []);

  const created = formRef?.current.getState().values.created;

  const isLaterThanLegallyInsignificantStatementSetting = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.LEGALLY_INSIGNIFICANT_STATEMENT_STARTS_FROM,
  );

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  /**
   * Функция скачивания файла решения
   */
  const handleDownloadWarningFile = useCallback(() => {
    dispatch(knmTryGenerateWarning(knmData?.id.toString() || '', isLaterThanLegallyInsignificantStatementSetting));
  }, [dispatch, knmData]);

  return (
    <div className={className} id={CONTENT_WARNING_BLOCK_ID}>
      <div className="shared-group-label">
        {isLaterThanLegallyInsignificantStatementSetting ? 'Выписка' : 'Содержание'}&nbsp;
        <span className="shared-no-wrap-text">
          {isLaterThanLegallyInsignificantStatementSetting ? '' : 'предостережения'}
          {(!isEnablePaperlessDocument || !isErknm) && knmData?.id && (
            <Button
              className="shared-btn-add"
              text="Сформировать"
              variant="link"
              color="primary"
              type="button"
              id="pmContentWarningAddButton"
              disabled={watchOnly}
              onClick={handleDownloadWarningFile}
            />
          )}
          {isLaterThanLegallyInsignificantStatementSetting || (
            <Button
              className="shared-btn-add"
              text="Добавить"
              variant="link"
              color="primary"
              type="button"
              id="pmContentWarningAddButton"
              disabled={watchOnly || attachmentsNumber > 0}
              onClick={openSignModal}
            />
          )}
        </span>
      </div>
      {attachmentsNumber === 0 && isErknm && <Hint text="Формируется автоматически после подписания" />}
      <KnmAttachmentsSigning
        isWatchOnly={watchOnly}
        name="contentWarningAttachments"
        isVisibleSignModal={isVisibleSignModal}
        closeSignModal={closeSignModal}
        formRef={formRef}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPmContentWarningStoreProps => {
  const isErknm = getLawInterface(state) === LawInterfacesEnum.FZ_248;

  return {
    knmData: getKnmData(state),
    settings: getAdminFetchSettings(state),
    formRef: getKnmFormRef(state),
    isErknm,
  };
};

export const PmContentWarning = connect(mapStateToProps)(PmContentWarningWithoutStore);
